import React, { useEffect, useState } from "react";
import { Calendar, MapPin, User, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Spinner, EmptyState, DemoBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function Events() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const authed = user && typeof user === "object";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/events").then(({ data }) => setData(data)).catch(() => setData({ items: [], total: 0, page: 1, pages: 1 })).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openEvent = async (ev) => {
    setSelected(ev);
    setRegistered(false);
    if (authed) {
      try {
        const { data } = await api.get(`/events/${ev.id}/registered`);
        setRegistered(data.registered);
      } catch { /* ignore */ }
    }
  };

  const register = async () => {
    if (!authed) { navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${selected.id}/register`);
      toast.success(data.message);
      setRegistered(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-nx section-nx">
      <div className="label-eyebrow mb-2">Events</div>
      <h1 className="heading-2">Community events & gatherings</h1>
      <p className="mt-3 max-w-2xl text-base text-slate-500">
        Networking meets, trade exhibitions, workshops and community gatherings.
      </p>

      <div className="mt-8">
        {loading || !data ? (
          <Spinner label="Loading events..." />
        ) : data.items.length === 0 ? (
          <EmptyState title="No upcoming events" message="Check back soon — new community events are added regularly." icon={Calendar} />
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((ev) => (
              <button key={ev.id} onClick={() => openEvent(ev)} className="card-nx card-nx-hover p-6 text-left" data-testid={`event-card-${ev.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex h-14 w-14 flex-col items-center justify-center rounded-lg bg-[#2563EB] text-white">
                    <span className="text-lg font-bold leading-none">{new Date(ev.date).getDate()}</span>
                    <span className="text-[10px] uppercase">{new Date(ev.date).toLocaleDateString("en-IN", { month: "short" })}</span>
                  </div>
                  {ev.is_demo && <DemoBadge />}
                </div>
                {ev.event_type && <span className="chip-gold mt-4">{ev.event_type}</span>}
                <h3 className="mt-3 text-base font-semibold text-[#0B192C] font-head">{ev.title}</h3>
                <p className="mt-2 flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" />{ev.location}</p>
                <p className="mt-1 flex items-center gap-1 text-xs text-slate-400"><Calendar className="h-3 w-3" />{formatDate(ev.date)}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                {selected.event_type && <span className="chip-gold w-fit">{selected.event_type}</span>}
                <DialogTitle className="mt-2 text-xl">{selected.title}</DialogTitle>
                <DialogDescription className="sr-only">Event details</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm text-slate-600">
                <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#B8860B]" />{formatDate(selected.date)}</p>
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#B8860B]" />{selected.location}</p>
                <p className="flex items-center gap-2"><User className="h-4 w-4 text-[#B8860B]" />Organised by {selected.organiser}</p>
                <p className="whitespace-pre-wrap pt-2 leading-relaxed">{selected.description}</p>
                {selected.contact_info && <p className="text-xs text-slate-400">Contact: {selected.contact_info}</p>}
              </div>
              {registered ? (
                <div className="mt-2 flex items-center justify-center gap-2 rounded-lg bg-emerald-50 py-3 text-sm font-medium text-emerald-700" data-testid="event-registered-badge">
                  <CheckCircle2 className="h-4 w-4" /> You've registered your interest
                </div>
              ) : (
                <button onClick={register} disabled={busy} className="btn-gold mt-2 w-full" data-testid="event-register-btn">
                  {busy ? "Registering..." : authed ? "Register interest" : "Login to register"}
                </button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
